import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { format } from "date-fns";
import useLang from "../../contexts/Language/LanguageContext";
import translate from "../../utils/lang/langHash";
import {
  section,
  projects,
  news,
  head,
  heading,
  marker,
  markerLight,
  markerDark,
  controls,
  control,
  toggle,
  counter,
  viewport,
  track,
  slide,
  card,
  cardHead,
  cardTitle,
  date,
  media,
  excerpt,
} from "./Carousel.module.scss";

const kinds = {
  projects: {
    className: projects,
    prev: "carousel-prev-projects",
    next: "carousel-next-projects",
  },
  news: {
    className: news,
    prev: "carousel-prev-news",
    next: "carousel-next-news",
  },
};

const INTERVAL_MS = 7000;

const ChevronIcon = ({ d }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect x="5.5" y="4" width="4.5" height="16" rx="1.2" fill="currentColor" />
    <rect x="14" y="4" width="4.5" height="16" rx="1.2" fill="currentColor" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M7 4.5v15l12.5-7.5z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

export default function Carousel({ kind, title, headingLevel, items }) {
  const lang = useLang();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  // Pointer over or focus inside pauses the tick without changing `playing`.
  const hold = useRef(false);
  const total = items.length;

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPlaying(false);
    }
  }, []);

  // Restarting on every index change gives a full interval after a manual step.
  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      if (!hold.current) setIndex((i) => (i + 1) % total);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playing, index, total]);

  if (total === 0) return null;

  const go = (i) => setIndex((i + total) % total);
  const Heading = `h${headingLevel}`;
  const CardTitle = `h${headingLevel + 1}`;
  const headingId = `carousel-${kind}`;
  const { className, prev, next } = kinds[kind];

  return (
    <section
      className={`${section} ${className}`}
      aria-roledescription="carousel"
      aria-labelledby={headingId}
      onPointerEnter={() => {
        hold.current = true;
      }}
      onPointerLeave={() => {
        hold.current = false;
      }}
      onFocus={() => {
        hold.current = true;
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) hold.current = false;
      }}
    >
      <div className={head}>
        <Heading id={headingId} className={heading}>
          <svg
            className={marker}
            viewBox="0 0 34 22"
            aria-hidden="true"
            focusable="false"
          >
            <polygon className={markerLight} points="8,0 34,0 26,22 0,22" />
            <polygon className={markerDark} points="8,0 16,22 0,22" />
          </svg>
          {title}
        </Heading>
        <div className={controls}>
          <button
            type="button"
            className={control}
            aria-label={translate(prev, lang)}
            onClick={() => go(index - 1)}
          >
            <ChevronIcon d="M15 4l-8 8 8 8" />
          </button>
          <span className={counter} aria-live={playing ? "off" : "polite"}>
            {`${index + 1} / ${total}`}
          </span>
          <button
            type="button"
            className={control}
            aria-label={translate(next, lang)}
            onClick={() => go(index + 1)}
          >
            <ChevronIcon d="M9 4l8 8-8 8" />
          </button>
          <button
            type="button"
            className={`${control} ${toggle}`}
            aria-label={translate(
              playing ? "carousel-pause" : "carousel-play",
              lang
            )}
            onClick={() => setPlaying((p) => !p)}
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>
      </div>
      <div className={viewport}>
        <div
          className={track}
          style={{ transform: `translateX(${-index * 100}%)` }}
        >
          {items.map((item, k) => (
            <div
              key={item.id}
              className={slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${k + 1} ${translate("carousel-of", lang)} ${total}`}
              aria-hidden={k === index ? undefined : "true"}
              inert={k === index ? undefined : ""}
            >
              <article className={card}>
                <div className={cardHead}>
                  <CardTitle className={cardTitle}>
                    <Link to={item.href}>{item.title}</Link>
                  </CardTitle>
                  <time className={date} dateTime={item.date}>
                    {format(new Date(item.date), "dd.MM.yyyy")}
                  </time>
                </div>
                {item.image && (
                  <div className={media}>
                    <GatsbyImage
                      image={item.image}
                      alt=""
                      style={{ width: "100%", height: "100%" }}
                      objectFit="cover"
                    />
                  </div>
                )}
                {item.text && <p className={excerpt}>{item.text}</p>}
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
